import React, { useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { Modal } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import {
    ImageOuterContainer,
    ImageInnerContainer,
    StickyHeaderContainer,
    StickyFooterContainer,
    ScrollableContainer,
    BgPictureContainer,
    SectionTitle,
    FullScreenVideoContainer,
    fullScreenModalStyles,
} from './styles'
import type { FullScreenModalProps } from './types'
import { getImageUrl } from './get-image-url'
import VideoPlayer from '../../../common/video-player'
import { Image } from './types'
import { getCategory } from '../../../../utils/category-utils'
import { usePropertyDetailsContext } from '../../../../hooks/property-details'
import { virtualTourVideoEventLabels } from '../../../../services/analytics-service/labels'

interface FullScreenImageItemProps {
    image: Image
    overlayIndex: number
    onItemClick: (index: number) => void
}

const FullScreenImageItem: React.FC<FullScreenImageItemProps> = ({ image, overlayIndex, onItemClick }) => {
    const handleClick = useCallback(() => {
        onItemClick(overlayIndex)
    }, [overlayIndex, onItemClick])

    const smallImage = getImageUrl('small-mobile', image)
    const mediumImage = getImageUrl('medium-mobile', image)
    const largeImage = getImageUrl('large-mobile', image)
    const desktopImage = getImageUrl('desktop', image)
    const wideScreenImage = getImageUrl('wide-screen', image)

    return (
        <ImageInnerContainer
            key={`image_full_screen_${overlayIndex}`}
            data-image-id={`full_screen_image_index_${overlayIndex}`}
            onClick={handleClick}
        >
            <BgPictureContainer>
                {!!wideScreenImage && <source media="(min-width:1024px)" srcSet={wideScreenImage} />}
                {!!desktopImage && <source media="(min-width:640px)" srcSet={desktopImage} />}
                {!!largeImage && <source media="(min-width:480px)" srcSet={largeImage} />}
                {!!mediumImage && <source media="(min-width:320px)" srcSet={mediumImage} />}
                {!!smallImage && <img loading="lazy" alt={image?.caption} src={smallImage} />}
            </BgPictureContainer>
        </ImageInnerContainer>
    )
}

const FullScreenModal: React.FC<FullScreenModalProps> = ({
    images,
    video,
    isFullScreenOpen,
    isOverlayOpen,
    setFullScreen,
    header,
    footer,
    setOverlay,
    setSelectedImage,
    currentIndex,
    onFullscreenImageClick,
    onFullScreenOpen,
    shouldAutoplayVideo = false,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const { isComboListing } = usePropertyDetailsContext()

    const closeModal = useCallback(() => {
        setFullScreen(false)
    }, [setFullScreen])

    useEffect(() => {
        if (isFullScreenOpen) {
            onFullScreenOpen?.()

            setTimeout(() => {
                if (scrollContainerRef.current) {
                    const targetElement = scrollContainerRef.current.querySelector(
                        `[data-image-id="full_screen_image_index_${currentIndex}"]`,
                    )

                    if (targetElement) {
                        targetElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                }
            }, 0)
        }
    }, [isFullScreenOpen])

    const handleItemClick = useCallback(
        (index: number) => {
            onFullscreenImageClick?.(index)
            setSelectedImage(index)
            setTimeout(() => setOverlay(true), 0)
        },
        [setSelectedImage, setOverlay, onFullscreenImageClick],
    )

    const renderedImages = useMemo(
        () =>
            images.map((image, index) => {
                const overlayIndex = video ? index + 1 : index
                return (
                    <FullScreenImageItem
                        key={`image_full_screen_${overlayIndex}`}
                        image={image}
                        overlayIndex={overlayIndex}
                        onItemClick={handleItemClick}
                    />
                )
            }),
        [images, video, handleItemClick],
    )

    return (
        <Modal isOpen={isFullScreenOpen} onRequestClose={closeModal} style={fullScreenModalStyles} ariaHideApp={false}>
            {!!header && (
                <StickyHeaderContainer>
                    {header({ onClose: closeModal, activeImageIndex: currentIndex })}
                </StickyHeaderContainer>
            )}
            <ScrollableContainer ref={scrollContainerRef}>
                {!!video && (
                    <>
                        <SectionTitle>{t('gallery.videos')}</SectionTitle>
                        <ImageOuterContainer>
                            <FullScreenVideoContainer data-image-id="full_screen_image_index_0">
                                <VideoPlayer
                                    src={video.src}
                                    isActive={!isOverlayOpen}
                                    autoplay={shouldAutoplayVideo}
                                    analyticsCategory={getCategory(isComboListing)}
                                    analyticsLabel={virtualTourVideoEventLabels.FullScreenGridModal}
                                />
                            </FullScreenVideoContainer>
                        </ImageOuterContainer>
                    </>
                )}
                {images.length > 0 && <SectionTitle>{t('gallery.photos')}</SectionTitle>}
                <ImageOuterContainer>{renderedImages}</ImageOuterContainer>
            </ScrollableContainer>
            {!!footer && (
                <StickyFooterContainer>
                    {footer({ onClose: closeModal, activeImageIndex: currentIndex })}
                </StickyFooterContainer>
            )}
        </Modal>
    )
}

export default memo(FullScreenModal)
